import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PLANS, planOf, PERIOD_LABEL } from '../../plans'
import type { Tenant } from '../../services/superadmin'
import {
  addPayment,
  deletePayment,
  listPayments,
  listSubscriptions,
  METHOD_LABEL,
  receiptUrl,
  saveSubscription,
  type PayMethod,
  type SubPayment,
  type Subscription,
} from '../../services/subscriptions'
import {
  addMonths,
  businessDay,
  monthStart,
  dayKey,
  daysBetween,
  listPriceKurus,
  payState,
  periodMonths,
  priceOn,
  type PayState,
  type PriceMode,
} from '../../utils/subscription'
import { amountInput, formatTRY, kurusToLiraInput, liraToKurus } from '../../utils/money'
import { Spinner } from '../Spinner'
import { DateField } from '../PickerFields'
import { ConfirmDialog } from '../pos/ConfirmDialog'
import { Select } from '../Select'
import { ColumnFilter } from './ColumnFilter'
import { PlusIcon, ReceiptTextIcon, TrashIcon, UploadIcon, WalletIcon, XIcon } from '../icons'
import './SubscriptionsPanel.css'

/**
 * Sistem Yönetimi > Abonelikler (1 Kas 2026, kullanıcı): kurumların ödeme
 * takibi. Her kurum bir satır: tutar, son ödeme, bir sonraki ödeme günü ve
 * durum. Satıra basınca sağdan çekmece: fiyat türü (liste, özel, ücretsiz),
 * ödeme alma ve geçmiş ödemeler, dekontlarıyla.
 *
 * Gecikme yalnız burada görünüyor; kurumun uygulaması etkilenmiyor
 * (kullanıcı kararı).
 */

const fmtDay = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })

const STATE_LABEL: Record<PayState['kind'], string> = {
  free: 'Ücretsiz',
  paid: 'Ödendi',
  soon: 'Yaklaşıyor',
  late: 'Gecikti',
  trial: 'Deneme',
  none: 'Kayıt yok',
}

function stateDetail(s: PayState): string {
  switch (s.kind) {
    case 'paid':
      return `${s.days} gün var`
    case 'soon':
      return s.days === 0 ? 'bugün' : `${s.days} gün kaldı`
    case 'late':
      if (s.closed) return 'salt okunur'
      return s.closesOn ? `${s.days} gün, ${fmtDay(s.closesOn)} kapanır` : `${s.days} gün`
    case 'trial':
      return `${s.days} gün kaldı`
    default:
      return ''
  }
}

interface Row {
  tenant: Tenant
  sub: Subscription
  /** Bugün geçerli tutar. */
  priceKurus: number
  /** Bugün özel fiyat mı geçerli (süresi dolan özel fiyat liste fiyatına döner). */
  priceCustom: boolean
  listKurus: number
  paidUntil: string | null
  last: SubPayment | null
  payments: SubPayment[]
  state: PayState
}

export function SubscriptionsPanel({ tenants }: { tenants: Tenant[] }) {
  const [subs, setSubs] = useState<Record<string, Subscription>>({})
  const [payments, setPayments] = useState<SubPayment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [paying, setPaying] = useState<Row | null>(null)
  const [query, setQuery] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [modeFilter, setModeFilter] = useState('')
  const [lastFilter, setLastFilter] = useState('')
  const [nextFilter, setNextFilter] = useState('')
  const today = dayKey(new Date())

  const reload = useCallback(async () => {
    setError(null)
    try {
      const [s, p] = await Promise.all([listSubscriptions(), listPayments()])
      setSubs(s)
      setPayments(p)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Abonelikler okunamadı.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const rows: Row[] = useMemo(
    () =>
      tenants
        .filter((t) => !t.deletion_requested_at)
        .map((t) => {
          const sub = subs[t.id] ?? { tenantId: t.id, mode: 'list' as PriceMode, customKurus: null, customUntil: null, note: '' }
          const own = payments.filter((p) => p.tenantId === t.id)
          const listKurus = listPriceKurus(t.plan, t.plan_period, t.features)
          const now = priceOn(sub.mode, sub.customKurus, sub.customUntil, listKurus, today)
          const paidUntil = own.reduce<string | null>((m, p) => (m && m > p.coversTo ? m : p.coversTo), null)
          const last = [...own].sort((a, b) => (a.paidOn < b.paidOn ? 1 : -1))[0] ?? null
          const trialEnd = t.trial_ends_at ? dayKey(new Date(t.trial_ends_at)) : null
          return { tenant: t, sub, priceKurus: now.kurus, priceCustom: now.custom, listKurus, paidUntil, last, payments: own, state: payState(sub.mode, paidUntil, trialEnd, today) }
        }),
    [tenants, subs, payments, today],
  )

  // Gecikenler en üstte, sonra yaklaşanlar; içlerinde en eskisi önde.
  const ORDER: Record<PayState['kind'], number> = { late: 0, soon: 1, none: 2, trial: 3, paid: 4, free: 5 }
  const month0 = today.slice(0, 7)
  const nextDay = (r: Row) =>
    r.state.kind === 'late' ? r.state.since : r.state.kind === 'soon' || r.state.kind === 'paid' || r.state.kind === 'trial' ? r.state.until : null
  const shown = rows
    .filter((r) => !stateFilter || r.state.kind === stateFilter)
    .filter((r) => !planFilter || (planFilter === 'yok' ? !r.tenant.plan : r.tenant.plan === planFilter))
    .filter((r) => !modeFilter || r.sub.mode === modeFilter)
    .filter((r) => {
      if (!lastFilter) return true
      if (lastFilter === 'yok') return !r.last
      if (!r.last) return false
      return lastFilter === 'ay' ? r.last.paidOn.startsWith(month0) : !r.last.paidOn.startsWith(month0)
    })
    .filter((r) => {
      if (!nextFilter) return true
      const d = nextDay(r)
      if (!d) return false
      const left = daysBetween(today, d)
      if (nextFilter === 'gecti') return left < 0
      if (nextFilter === 'hafta') return left >= 0 && left <= 7
      if (nextFilter === 'ay') return left >= 0 && left <= 30
      return left > 30
    })
    .filter((r) => {
      const q = query.trim().toLocaleLowerCase('tr')
      return !q || r.tenant.display_name.toLocaleLowerCase('tr').includes(q) || r.tenant.id.includes(q)
    })
    .sort((a, b) => ORDER[a.state.kind] - ORDER[b.state.kind] || (a.paidUntil ?? '').localeCompare(b.paidUntil ?? ''))

  const late = rows.filter((r) => r.state.kind === 'late')
  const soon = rows.filter((r) => r.state.kind === 'soon')
  const month = today.slice(0, 7)
  const monthTotal = payments.filter((p) => p.paidOn.startsWith(month)).reduce((s, p) => s + p.amountKurus, 0)

  const selected = rows.find((r) => r.tenant.id === selectedId) ?? null

  // Çekmece Esc ile kapanır (ödeme penceresi açıkken önce o).
  useEffect(() => {
    if (!selectedId || paying) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSelectedId(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedId, paying])

  if (loading) return <Spinner label="Abonelikler yükleniyor…" />

  return (
    <section className="superadmin__panel subs">
      <div className="superadmin__panel-head">
        <h2>Abonelikler</h2>
        <span className="superadmin__count">{rows.length}</span>
      </div>

      {error && <p className="superadmin__error">{error}</p>}

      <div className="subs__stats">
        <button
          type="button"
          className={`subs__stat is-late ${stateFilter === 'late' ? 'is-on' : ''}`}
          onClick={() => setStateFilter(stateFilter === 'late' ? '' : 'late')}
        >
          <span>Geciken</span>
          <b>{late.length}</b>
          <small>{late.length ? formatTRY(late.reduce((s, r) => s + r.priceKurus, 0)) : 'Yok'}</small>
        </button>
        <button
          type="button"
          className={`subs__stat is-soon ${stateFilter === 'soon' ? 'is-on' : ''}`}
          onClick={() => setStateFilter(stateFilter === 'soon' ? '' : 'soon')}
        >
          <span>7 gün içinde</span>
          <b>{soon.length}</b>
          <small>{soon.length ? formatTRY(soon.reduce((s, r) => s + r.priceKurus, 0)) : 'Yok'}</small>
        </button>
        <div className="subs__stat is-month">
          <span>Bu ay alınan</span>
          <b>{formatTRY(monthTotal)}</b>
          <small>{payments.filter((p) => p.paidOn.startsWith(month)).length} ödeme</small>
        </div>
      </div>

      <div className="ftable ftable--tenants subs__table">
        <div className="ftable__wrap">
          <table>
            <thead>
              <tr>
                <th>
                  <ColumnFilter label="İşletme" query={query} onQuery={setQuery} placeholder="Ad, kod" />
                </th>
                <th className="subs__col-plan">
                  <ColumnFilter
                    label="Paket"
                    value={planFilter}
                    onChange={setPlanFilter}
                    options={[...PLANS.map((p) => ({ value: p.id, label: p.name })), { value: 'yok', label: 'Paketsiz' }]}
                  />
                </th>
                <th className="subs__col-price">
                  <ColumnFilter
                    label="Tutar"
                    value={modeFilter}
                    onChange={setModeFilter}
                    options={[
                      { value: 'list', label: 'Liste fiyatı' },
                      { value: 'custom', label: 'Özel fiyat' },
                      { value: 'free', label: 'Ücretsiz' },
                    ]}
                  />
                </th>
                <th className="subs__col-day">
                  <ColumnFilter
                    label="Son ödeme"
                    value={lastFilter}
                    onChange={setLastFilter}
                    options={[
                      { value: 'ay', label: 'Bu ay' },
                      { value: 'eski', label: 'Daha önce' },
                      { value: 'yok', label: 'Hiç ödeme yok' },
                    ]}
                  />
                </th>
                <th className="subs__col-day">
                  <ColumnFilter
                    label="Sonraki ödeme"
                    value={nextFilter}
                    onChange={setNextFilter}
                    options={[
                      { value: 'gecti', label: 'Geçti' },
                      { value: 'hafta', label: '7 gün içinde' },
                      { value: 'ay', label: '30 gün içinde' },
                      { value: 'sonra', label: '30 günden sonra' },
                    ]}
                  />
                </th>
                <th className="subs__col-state">
                  <ColumnFilter
                    label="Durum"
                    value={stateFilter}
                    onChange={setStateFilter}
                    options={(['late', 'soon', 'paid', 'trial', 'none', 'free'] as const).map((k) => ({
                      value: k,
                      label: STATE_LABEL[k],
                    }))}
                  />
                </th>
                <th className="subs__col-act" aria-label="İşlem" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr
                  key={r.tenant.id}
                  className={`ttable__row ${r.tenant.id === selectedId ? 'is-selected' : ''}`}
                  onClick={() => setSelectedId(r.tenant.id)}
                >
                  <td>
                    <button type="button" className="ttable__name" onClick={() => setSelectedId(r.tenant.id)}>
                      <strong>{r.tenant.display_name}</strong>
                      <span>{r.tenant.id}</span>
                    </button>
                  </td>
                  <td>
                    {planOf(r.tenant.plan) ? (
                      <span className={`ftable__plan is-${r.tenant.plan}`}>
                        {planOf(r.tenant.plan)!.name}
                        {r.tenant.plan_period ? `, ${PERIOD_LABEL[r.tenant.plan_period].toLocaleLowerCase('tr')}` : ''}
                      </span>
                    ) : (
                      <span className="ttable__muted">Paketsiz</span>
                    )}
                  </td>
                  <td>
                    {r.sub.mode === 'free' ? (
                      <span className="ttable__muted">Ücretsiz</span>
                    ) : (
                      <span className="ttable__two">
                        <b>{formatTRY(r.priceKurus)}</b>
                        <small>
                          {r.priceCustom
                            ? r.sub.customUntil
                              ? `Özel, bitiş ${fmtDay(r.sub.customUntil)}`
                              : 'Özel fiyat'
                            : 'Liste fiyatı'}
                        </small>
                      </span>
                    )}
                  </td>
                  <td>
                    {r.last ? (
                      <span className="ttable__two">
                        <b>{fmtDay(r.last.paidOn)}</b>
                        <small>{formatTRY(r.last.amountKurus)}</small>
                      </span>
                    ) : (
                      <span className="ttable__muted">Yok</span>
                    )}
                  </td>
                  <td>
                    {r.state.kind === 'free' || r.state.kind === 'none' ? (
                      <span className="ttable__muted">{r.state.kind === 'free' ? 'Yok' : 'Bilinmiyor'}</span>
                    ) : (
                      <b className="subs__day">
                        {fmtDay(r.state.kind === 'late' ? r.state.since : r.state.until)}
                      </b>
                    )}
                  </td>
                  <td>
                    <StatePill state={r.state} />
                  </td>
                  <td className="subs__col-act">
                    {r.sub.mode !== 'free' && (
                      <button
                        type="button"
                        className="subs__pay"
                        onClick={(e) => {
                          e.stopPropagation()
                          setPaying(r)
                        }}
                      >
                        <PlusIcon />
                        <span>Ödeme</span>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={7} className="ftable__empty">
                    Bu süzgece uyan işletme yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {[query, stateFilter, planFilter, modeFilter, lastFilter, nextFilter].some((v) => v !== '') && (
          <div className="ftable__foot">
            <span>
              {shown.length}/{rows.length} işletme gösteriliyor
            </span>
            <button
              type="button"
              className="ftable__clear"
              onClick={() => {
                setQuery('')
                setStateFilter('')
                setPlanFilter('')
                setModeFilter('')
                setLastFilter('')
                setNextFilter('')
              }}
            >
              <XIcon />
              Filtreleri temizle
            </button>
          </div>
        )}
      </div>

      {selected && (
        <SubDrawer
          row={selected}
          onClose={() => setSelectedId(null)}
          onPay={() => setPaying(selected)}
          onChanged={reload}
          onError={setError}
        />
      )}

      {paying && (
        <PaymentDialog
          row={paying}
          today={today}
          onClose={() => setPaying(null)}
          onSaved={async (msg) => {
            setPaying(null)
            if (msg) setError(msg)
            await reload()
          }}
        />
      )}
    </section>
  )
}

function StatePill({ state }: { state: PayState }) {
  const detail = stateDetail(state)
  return (
    <span className={`subs__pill is-${state.kind} ${state.kind === 'late' && state.closed ? 'is-closed' : ''}`}>
      <i />
      {state.kind === 'late' && state.closed ? 'Kapandı' : STATE_LABEL[state.kind]}
      {detail && <small>{detail}</small>}
    </span>
  )
}

/** Kurumun aboneliği: fiyat türü, ödeme alma, geçmiş ödemeler. */
function SubDrawer({
  row,
  onClose,
  onPay,
  onChanged,
  onError,
}: {
  row: Row
  onClose: () => void
  onPay: () => void
  onChanged: () => Promise<void>
  onError: (msg: string | null) => void
}) {
  const { tenant, sub } = row
  const period = PERIOD_LABEL[tenant.plan_period ?? 'aylik'].toLocaleLowerCase('tr')
  const today = dayKey(new Date())
  const [custom, setCustom] = useState(sub.customKurus != null ? kurusToLiraInput(sub.customKurus) : '')
  /** Özel fiyatın süresi: '' süresiz, ay sayısı ya da 'tarih' (elle seçilen gün). */
  const [dur, setDur] = useState(sub.customUntil ? 'tarih' : '')
  const [until, setUntil] = useState(sub.customUntil ?? '')
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState<SubPayment | null>(null)

  useEffect(() => {
    setCustom(sub.customKurus != null ? kurusToLiraInput(sub.customKurus) : '')
    setDur(sub.customUntil ? 'tarih' : '')
    setUntil(sub.customUntil ?? '')
  }, [sub.customKurus, sub.customUntil, tenant.id])

  const save = async (
    mode: PriceMode,
    customKurus: number | null = sub.customKurus,
    customUntil: string | null = sub.customUntil,
  ) => {
    setBusy(true)
    onError(null)
    try {
      await saveSubscription({ ...sub, mode, customKurus, customUntil })
      await onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Kaydedilemedi.')
    } finally {
      setBusy(false)
    }
  }

  const customKurus = liraToKurus(custom)
  const untilValue = dur === '' ? null : until || null
  const customDirty = sub.mode === 'custom' && (customKurus !== sub.customKurus || untilValue !== sub.customUntil)
  const untilBad = dur !== '' && (!until || until <= today)

  const open = async (p: SubPayment) => {
    if (!p.receiptPath) return
    try {
      window.open(await receiptUrl(p.receiptPath), '_blank', 'noopener')
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Dekont açılamadı.')
    }
  }

  const remove = async (p: SubPayment) => {
    setConfirm(null)
    try {
      await deletePayment(p)
      await onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Silinemedi.')
    }
  }

  return (
    <div className="tdrawer" role="dialog" aria-modal="true" aria-label={tenant.display_name}>
      <div className="tdrawer__backdrop" onClick={onClose} />
      <aside className="tdrawer__panel">
        <header className="tdrawer__head">
          <div>
            <h2>{tenant.display_name}</h2>
            <span>{tenant.id}</span>
          </div>
          <button type="button" className="tdrawer__x" aria-label="Kapat" onClick={onClose}>
            <XIcon />
          </button>
        </header>

        <div className="tdrawer__body subs__drawer">
          <div className="subs__now">
            <StatePill state={row.state} />
            <dl>
              <div>
                <dt>Sonraki ödeme</dt>
                <dd>
                  {row.state.kind === 'free'
                    ? 'Yok'
                    : row.state.kind === 'none'
                      ? 'Bilinmiyor'
                      : fmtDay(row.state.kind === 'late' ? row.state.since : row.state.until)}
                </dd>
              </div>
              <div>
                <dt>Tutar</dt>
                <dd>
                  {sub.mode === 'free' ? 'Ücretsiz' : `${formatTRY(row.priceKurus)} / ${period}`}
                  {row.priceCustom && sub.customUntil && <small>Özel fiyat, bitiş {fmtDay(sub.customUntil)}</small>}
                </dd>
              </div>
            </dl>
            {sub.mode !== 'free' && (
              <button type="button" className="btn btn--primary subs__now-pay" onClick={onPay}>
                <WalletIcon />
                Ödeme al
              </button>
            )}
          </div>

          <div className="subs__block">
            <h3>Fiyat</h3>
            <div className="subs__modes" role="radiogroup" aria-label="Fiyat türü">
              {(
                [
                  ['list', 'Liste fiyatı', `${formatTRY(row.listKurus)} / ${period}`],
                  ['custom', 'Özel fiyat', 'Anlaştığınız tutar'],
                  ['free', 'Ücretsiz', 'Ödeme beklenmez'],
                ] as const
              ).map(([mode, label, hint]) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={sub.mode === mode}
                  className={`subs__mode ${sub.mode === mode ? 'is-on' : ''}`}
                  disabled={busy}
                  onClick={() =>
                    sub.mode !== mode && void save(mode, mode === 'custom' ? sub.customKurus ?? row.listKurus : sub.customKurus)
                  }
                >
                  <b>{label}</b>
                  <small>{hint}</small>
                </button>
              ))}
            </div>
            {sub.mode === 'custom' && (
              <div className="subs__custom">
                <div className={`subs__custom-row ${dur !== '' ? 'has-until' : ''}`}>
                  <label className="dlgfield">
                    <span>Özel tutar ({period})</span>
                    <input
                      value={custom}
                      inputMode="decimal"
                      onChange={(e) => setCustom(amountInput(e.target.value))}
                      placeholder="0"
                    />
                  </label>
                  {/* Süre (1 Kas 2026, kullanıcı: "iki ay 1.000 ₺, yılbaşından
                      sonra paket fiyatı"). Ay seçilince bitiş günü dolar. */}
                  <div className="subs__field">
                    <b id="sub-dur">Süre</b>
                    <Select
                      aria-labelledby="sub-dur"
                      value={dur}
                      onChange={(v) => {
                        setDur(v)
                        if (v === '') setUntil('')
                        else if (v !== 'tarih') setUntil(addMonths(today, Number(v)))
                      }}
                      options={[
                        { value: '', label: 'Süresiz' },
                        ...[1, 2, 3, 6, 12].map((n) => ({ value: String(n), label: `${n} ay` })),
                        { value: 'tarih', label: 'Tarih seç' },
                      ]}
                    />
                  </div>
                  {dur !== '' && (
                    <div className="subs__field">
                      <b title={`Bu gün liste fiyatına döner: ${formatTRY(row.listKurus)}`}>
                        Bitiş, sonra {formatTRY(row.listKurus)}
                      </b>
                      <DateField
                        label="Özel fiyatın bitişi"
                        value={until}
                        onChange={(v) => {
                          setUntil(v)
                          setDur('tarih')
                        }}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn--primary subs__custom-save"
                    disabled={busy || !customDirty || customKurus == null || untilBad}
                    title={untilBad ? 'Bitiş günü bugünden sonra olmalı' : undefined}
                    onClick={() => void save('custom', customKurus, untilValue)}
                  >
                    Kaydet
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="subs__block">
            <h3>
              Ödemeler <span className="superadmin__count">{row.payments.length}</span>
            </h3>
            {row.payments.length === 0 ? (
              <p className="subs__empty">Henüz ödeme kaydı yok.</p>
            ) : (
              <ul className="subs__payments">
                {row.payments.map((p) => (
                  <li key={p.id}>
                    <div className="subs__payment-main">
                      <b>{formatTRY(p.amountKurus)}</b>
                      <span>
                        {fmtDay(p.paidOn)}, {METHOD_LABEL[p.method]}
                      </span>
                      <small>
                        Dönem: {fmtDay(p.coversFrom)} - {fmtDay(p.coversTo)}
                      </small>
                      {p.note && <small className="subs__payment-note">{p.note}</small>}
                    </div>
                    <div className="subs__payment-actions">
                      {p.receiptPath && (
                        <button type="button" className="subs__icon-btn" onClick={() => void open(p)} title="Dekontu aç">
                          <ReceiptTextIcon />
                          <span>Dekont</span>
                        </button>
                      )}
                      <button
                        type="button"
                        className="subs__icon-btn is-danger"
                        onClick={() => setConfirm(p)}
                        aria-label="Ödemeyi sil"
                        title="Ödemeyi sil"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </aside>

      {confirm && (
        <ConfirmDialog
          title="Ödeme silinsin mi?"
          body={`${formatTRY(confirm.amountKurus)}, ${fmtDay(confirm.paidOn)}. Dekontu da silinir, geri alınamaz.`}
          confirmLabel="Sil"
          danger
          onConfirm={() => void remove(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

const METHODS: PayMethod[] = ['havale', 'nakit', 'kart', 'diger']

/** Ödeme alma penceresi: tutar, yöntem, tarih, kapsadığı dönem, dekont. */
function PaymentDialog({
  row,
  today,
  onClose,
  onSaved,
}: {
  row: Row
  today: string
  onClose: () => void
  onSaved: (message: string | null) => Promise<void>
}) {
  const months0 = periodMonths(row.tenant.plan_period)
  // Dönem, bir önceki ödemenin bittiği günden başlıyor; ödeme yoksa
  // deneme bitişinden (geçmişteyse) ya da bugünden. Dönemler takvim ayı
  // (2 Kas 2026, kullanıcı: "her ayın ilk iş günü ödeme alınacak"): bitiş
  // hep bir ayın 1'i, ödeme günü onun ilk iş günü. Ay ortasında başlayan
  // ilk dönem ay başında biter.
  const trialEnd = row.tenant.trial_ends_at ? dayKey(new Date(row.tenant.trial_ends_at)) : null
  const from0 = row.paidUntil ?? (trialEnd && trialEnd < today ? trialEnd : monthStart(today))

  // Tutar dönemin başladığı güne göre: özel fiyatı bitmiş bir döneme liste
  // fiyatı gelir.
  const [amount, setAmount] = useState(() =>
    kurusToLiraInput(priceOn(row.sub.mode, row.sub.customKurus, row.sub.customUntil, row.listKurus, from0).kurus),
  )
  const [method, setMethod] = useState<PayMethod>('havale')
  const [paidOn, setPaidOn] = useState(today)
  const [from, setFrom] = useState(from0)
  const [months, setMonths] = useState(String(months0))
  const [note, setNote] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  const amountKurus = liraToKurus(amount)
  const m = Number(months)
  const to = from && m > 0 ? addMonths(monthStart(from), m) : null
  const valid = amountKurus != null && !!paidOn && !!from && !!to && m > 0 && m <= 36

  const submit = async () => {
    if (!valid || amountKurus == null || !to) return
    setBusy(true)
    setError(null)
    try {
      const { receiptError } = await addPayment(
        {
          tenantId: row.tenant.id,
          amountKurus,
          method,
          paidOn,
          coversFrom: from,
          coversTo: to,
          note: note.trim(),
        },
        file,
      )
      await onSaved(receiptError ? `Ödeme kaydedildi ama dekont yüklenemedi: ${receiptError}` : null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi.')
      setBusy(false)
    }
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="pay-title" onClick={() => !busy && onClose()}>
      <div className="dlg__card dlg__card--x subs__dlg" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="dlg__x" onClick={onClose} aria-label="Kapat" disabled={busy}>
          <XIcon />
        </button>
        <h2 id="pay-title">Ödeme al</h2>
        <p className="subs__dlg-who">{row.tenant.display_name}</p>

        <div className="subs__grid">
          <label className="dlgfield">
            <span>Tutar (₺)</span>
            <input value={amount} inputMode="decimal" onChange={(e) => setAmount(amountInput(e.target.value))} />
          </label>
          {/* .dlgfield değil: onun "span" kuralı Select ve tarih kutusunun
              içindeki yazıları da büyük harfe çeviriyordu. */}
          <div className="subs__field">
            <b id="pay-method">Yöntem</b>
            <Select
              aria-labelledby="pay-method"
              value={method}
              onChange={(v) => setMethod(v as PayMethod)}
              options={METHODS.map((k) => ({ value: k, label: METHOD_LABEL[k] }))}
            />
          </div>
          <div className="subs__field">
            <b>Ödeme tarihi</b>
            <DateField label="Ödeme tarihi" value={paidOn} onChange={setPaidOn} />
          </div>
          <div className="subs__field">
            <b>Dönem başlangıcı</b>
            <DateField label="Dönem başlangıcı" value={from} onChange={setFrom} />
          </div>
          <label className="dlgfield">
            <span>Kaç ay</span>
            <input value={months} inputMode="numeric" onChange={(e) => setMonths(e.target.value.replace(/\D/g, '').slice(0, 2))} />
          </label>
          <div className="subs__until">
            <span>Sonraki ödeme günü</span>
            <b>{to ? fmtDay(businessDay(to)) : '-'}</b>
          </div>
        </div>

        <label className="dlgfield">
          <span>Not</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="İsteğe bağlı" maxLength={500} />
        </label>

        <div className="subs__file">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className="subs__file-chosen">
              <ReceiptTextIcon />
              <span>{file.name}</span>
              <button
                type="button"
                className="subs__icon-btn is-danger"
                aria-label="Dekontu kaldır"
                onClick={() => {
                  setFile(null)
                  if (fileRef.current) fileRef.current.value = ''
                }}
              >
                <XIcon />
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn--ghost subs__file-btn" onClick={() => fileRef.current?.click()}>
              <UploadIcon />
              Dekont ekle (isteğe bağlı)
            </button>
          )}
        </div>

        {error && <p className="superadmin__error">{error}</p>}

        <div className="dlg__buttons dlg__buttons--even">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Vazgeç
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={busy || !valid}>
            {busy ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  )
}
